import React, { useEffect, useState } from 'react';
import { Row, Col, Button } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useSelector } from "react-redux";

const Step1New = ({ form, setForm, nextStep }) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  // const token = loggedInUser?.token;
  const token = loggedInUser?.refreshToken; 

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [propertyPurposes, setPropertyPurposes] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [propertySubtypes, setPropertySubtypes] = useState([]);
  const [propertySubSubTypes, setPropertySubSubTypes] = useState([]);
  const [landTypes, setLandTypes] = useState([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState(form.propertyType || "");
  // eslint-disable-next-line no-unused-vars
  const [selectedPropertyPurpose, setSelectedPropertyPurpose] = useState(form.propertyPurpose || "");
  const [selectedPropertySubtype, setSelectedPropertySubtype] = useState(form.propertySubtype || "");
  const [propertySubSubType, setPropertySubSubType] = useState();

  const postData = {
    page_number: 1,
    page_size: 20,
    order_by: "ASC",
  };

  // Fetch property purposes and types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const authToken = token;
        const purposeResponse = await axios.post(
          `${baseUrl}/property/purposes`,
          postData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (purposeResponse.data.statusType === "SUCCESS") {
          setPropertyPurposes(purposeResponse.data.propertyPurposes);
        } else {
          setPropertyPurposes([]);
        }

        const typeResponse = await axios.post(
          `${baseUrl}/property/types`,
          postData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (typeResponse.data.statusType === "SUCCESS") {
          setPropertyTypes(typeResponse.data.propertyTypes);
        } else {
          setPropertyTypes([]);
        }
      } catch (error) {
        setPropertyPurposes([]);
        setPropertyTypes([]);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token]);

  // Fetch subtypes
  useEffect(() => {
    const fetchSubtypes = async (propertyTypeId) => {
      if (!propertyTypeId) {
        setPropertySubtypes([]);
        return;
      }
      try {
        const response = await axios.post(
          `${baseUrl}/property/sub/types/${propertyTypeId}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setPropertySubtypes(response.data.propertySubTypes);
        } else {
          setPropertySubtypes([]);
        }
      } catch (error) {
        setPropertySubtypes([]);
      }
    };
    fetchSubtypes(selectedPropertyType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyType, baseUrl, token]);

  // Fetch subsubtypes
  useEffect(() => {
    const fetchPropertySubSubTypes = async () => {
      if (!selectedPropertySubtype) {
        setPropertySubSubTypes([]);
        return;
      }
      try {
        const response = await axios.get(
          `https://services.allhomedesire.in/all/property/sub/sub/types/${selectedPropertySubtype}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setPropertySubSubTypes(response.data.propertySubSubTypes);
          setPropertySubSubType(response.data.propertySubSubTypes[0]?.property_sub_type_name);
        } else {
          setPropertySubSubTypes([]);
        }
      } catch (error) {
        setPropertySubSubTypes([]);
      }
    };
    fetchPropertySubSubTypes();
  }, [selectedPropertySubtype, token]);

  // Fetch land types
  useEffect(() => {
    const fetchLandTypes = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/all/land/sub/types`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setLandTypes(response.data.landTypes);
        } else {
          setLandTypes([]);
        }
      } catch (error) {
        setLandTypes([]);
      }
    };
    fetchLandTypes();
  }, [baseUrl, token]);

  const validationSchema = Yup.object({
    propertyType: Yup.string().required("Property type is required"),
    propertyPurpose: Yup.string().required("Property Purpose is required"),
    propertySubtype: Yup.string().required("Property Subtype is required"),
    propertySubSubType: propertySubSubTypes.length > 0 
      ? Yup.string().required("Property Sub Subtype is required")
      : Yup.string().nullable(),
    are: Yup.string().required("Area is required"),
    landType: Yup.string().required("Land Type is required"),
    loanAvailability: Yup.boolean().nullable().required("Loan Availability is required"),
  });

  const initialValues = {
    propertyPurpose: form?.propertyPurpose || "",
    propertyType: form?.propertyType || "",
    propertySubtype: form?.propertySubtype || "",
    propertySubSubType: form?.propertySubSubType || "",
    are: form?.are || "",
    landType: form?.landType || "",
    loanAvailability: typeof form?.loanAvailability === 'boolean'
      ? form.loanAvailability
      : form?.loanAvailability === "Yes"
        ? true
        : form?.loanAvailability === "No"
          ? false
          : null,
  };

  const handleSubmit = (values, { setSubmitting }) => {
    setForm(prev => ({
      ...prev,
      ...values,
    }));
    setSubmitting(false);
    if (nextStep) nextStep();
  };
 
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isSubmitting, setFieldValue, values }) => (
        <Form className="p-4 bg-light rounded shadow-sm">
          <Row className="mb-4">
            <h4 className="mb-3">I Wish To:</h4>
            <Col md={12} className="mb-2">
              <FieldArray name="propertyPurpose">
                {() => (
                  <div className="d-flex flex-wrap gap-3">
                    {propertyPurposes.map((purpose) => (
                      <Button
                        key={purpose.id}
                        variant={values.propertyPurpose === purpose.id ? 'primary' : 'outline-secondary'}
                        className="btn text-capitalize w-25"
                        onClick={() => {
                          setFieldValue('propertyPurpose', purpose.id);
                          setFieldValue('propertyPurposeName', purpose.name);
                          setSelectedPropertyPurpose(purpose.id);
                        }}
                      >
                        {purpose.name}
                      </Button>
                    ))}
                  </div>
                )}
              </FieldArray>
            </Col>
            <ErrorMessage
              name="propertyPurpose"
              component="div"
              className="text-danger mt-1"
            />
          </Row>

          <Row className="mb-4 row-gap-4">
            {values.propertyPurpose && (
              <Col md={6}>
                <h5 className="mb-2">Property Type</h5>
                <Field
                  as="select"
                  className="form-select"
                  id="propertyType"
                  name="propertyType"
                  onChange={(e) => {
                    const selectedOption = e.target.selectedOptions[0];
                    const name = selectedOption.getAttribute("data-name");
                    setFieldValue("propertyType", e.target.value);
                    setFieldValue("propertyTypeName", name);
                    setSelectedPropertyType(e.target.value); 
                  }}
                >
                  <option value="" label="Select property type" disabled />
                  {propertyTypes.map((type) => (
                    <option key={type.id} value={type.id} data-name={type.name}>
                      {type.name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="propertyType"
                  component="div"
                  className="text-danger mt-1"
                />
              </Col>
            )}
            {values.propertyType && (
              <Col md={6}>
                <h5 className="mb-2">Property Subtype</h5>
                <Field
                  as="select"
                  className="form-select"
                  id="propertySubtype"
                  name="propertySubtype"
                  onChange={(e) => {
                    const selectedOption = e.target.selectedOptions[0];
                    const name = selectedOption.getAttribute("data-name");
                    setFieldValue("propertySubtype", e.target.value);
                    setFieldValue("propertySubtypeName", name);
                    setSelectedPropertySubtype(e.target.value);
                  }}
                  disabled={!selectedPropertyType}
                >
                  <option value="" disabled>Select Property Subtype</option>
                  {propertySubtypes.map((subtype) => (
                    <option key={subtype.id} value={subtype.id} data-name={subtype.name}>
                      {subtype.name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="propertySubtype"
                  component="div"
                  className="text-danger mt-1"
                />
              </Col>
            )}
          </Row>

          {values.propertySubtype && (
            <Row className="mb-4 row-gap-2 mt-4">
              <Col md={4} className="mb-3">
                <h5 className="mb-2">{propertySubSubType} Types</h5>
                <Field
                  as="select"
                  className="form-select"
                  id="propertySubSubType"
                  name="propertySubSubType"
                  onChange={(e) => {
                    const selectedOption = e.target.selectedOptions[0];
                    const name = selectedOption.getAttribute("data-name");
                    setFieldValue("propertySubSubType", e.target.value);
                    setFieldValue("propertySubSubTypeName", name);
                  }}
                  value={values.propertySubSubType || ""}
                >
                  <option value="" disabled>Select Property Sub Sub Type</option>
                  {propertySubSubTypes.length > 0 ? (
                    propertySubSubTypes.map((subSubType) => (
                      <option key={subSubType.id} value={subSubType.id} data-name={subSubType.name}>
                        {subSubType.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No Sub Sub Types Available</option>
                  )}
                </Field>
                <ErrorMessage
                  name="propertySubSubType"
                  className="text-danger mt-1"
                  component="div"
                />
              </Col>

              <Col md={4} className="mb-3">
                <h5 className="mb-2">Area</h5>
                <input
                  type="text"
                  className="form-control bg-white h-10"
                  placeholder="Enter Area"
                  value={values.are}
                  name="are"
                  onChange={(e) => {
                    setFieldValue("are", e.target.value);
                  }}
                />
                <ErrorMessage
                  name="are"
                  component="div"
                  className="text-danger mt-1"
                />
              </Col>

              <Col md={4} className="mb-3">
                <h5 className="mb-2">Land Type</h5>
                <Field
                  as="select"
                  className="form-select"
                  id="landType"
                  name="landType"
                  onChange={(e) => {
                    const selectedOption = e.target.selectedOptions[0];
                    const name = selectedOption.getAttribute("data-name");
                    setFieldValue("landType", e.target.value);
                    setFieldValue("landTypeName", name);
                  }}
                >
                  <option value="" disabled>Select Land Type</option>
                  {landTypes.map((landType) => (
                    <option key={landType.id} value={landType.id} data-name={landType.name}>
                      {landType.name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="landType"
                  component="div"
                  className="text-danger mt-1"
                />
              </Col>

              <Col md={12} className="mb-3">
                <h5 className="mb-2">Loan Availability</h5>
                <div className="d-flex flex-wrap gap-3">
                  {[
                    { label: "Yes", value: true },
                    { label: "No", value: false }
                  ].map((option) => (
                    <Button
                      key={option.label}
                      variant={values.loanAvailability === option.value ? 'primary' : 'outline-secondary'}
                      className="btn text-capitalize w-25"
                      onClick={() => setFieldValue('loanAvailability', option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </Col> 
              <ErrorMessage
                name="loanAvailability"
                component="div"
                className="text-danger mt-1"
              />
            </Row>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              Next
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Step1New;